<script setup lang="ts">
import { HttpStatusCode, isAxiosError } from 'axios';
import { BAlert, BModal, BForm, BFormGroup, BFormInput } from 'bootstrap-vue-next';
import { ref, computed } from 'vue';

import type { Mentorship } from '~/api/users';
import { removeMentor } from '~/api/users';
import LoadingButton from '~/common/components/LoadingButton.vue';

const props = defineProps<{
  userHandle: string;
  mentor: Mentorship;
}>();

const emit = defineEmits<{ mentorshipRemoved: [] }>();
const model = defineModel<boolean>();

const confirmedUsername = ref('');
const error = ref<string | null>(null);
const loading = ref(false);
const isConfirmed = computed(() => {
  if (confirmedUsername.value.length === 0) {
    return null;
  }

  return confirmedUsername.value === props.mentor.username;
});

const removeMentorship = async () => {
  if (!isConfirmed.value || loading.value) {
    return;
  }

  error.value = null;
  loading.value = true;

  try {
    await removeMentor(props.userHandle, props.mentor.id);
    emit('mentorshipRemoved');
    model.value = false;
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === HttpStatusCode.NotFound) {
      error.value = 'Mentorship not found';
    } else {
      error.value = 'Failed to remove mentor';
    }
  } finally {
    loading.value = false;
  }
};

const resetForm = () => {
  confirmedUsername.value = '';
  error.value = null;
};
</script>

<template>
  <BModal
    v-model="model"
    title="Remove mentorship"
    no-footer
    @hidden="resetForm"
  >
    <BAlert
      :model-value="!!error"
      variant="danger"
    >
      {{ error }}
    </BAlert>

    <BForm @submit.prevent="removeMentorship">
      <BFormGroup
        :label="`Enter '${mentor.username}' to confirm`"
        :state="isConfirmed"
        invalid-feedback="Username does not match."
        class="mb-3"
      >
        <BFormInput
          v-model="confirmedUsername"
          :state="isConfirmed"
          required
        />
      </BFormGroup>

      <LoadingButton
        type="submit"
        class="float-end"
        variant="danger"
        :loading="loading"
      >
        Remove mentor
      </LoadingButton>
    </BForm>
  </BModal>
</template>
